import React from 'react';
import { Box, Dialog, Flex, Portal, Separator, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useTranslation } from 'localization';
import { Profile, Transaction } from 'types';

import { fixDecimal } from 'utils';

import CopyClipBoard from '../CopyClipBoard';
import {
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from '../ServiceDialog';
import TransactionMethod from '../TransactionMethod';
import TransactionStatus from '../TransactionStatus';
import XmAdditionalData from '../XmAdditionalData';

type TransactionDetailModalProps = {
  transaction: Transaction;
  profile: Profile;
  isOpen: boolean;
  onClose: () => void;
};

type ModalT = (key: string) => string;

function hasDestinationWallet(transaction: Transaction): boolean {
  return (
    typeof transaction.destination_wallet === 'object' &&
    transaction.destination_wallet !== null
  );
}

function getAmountTr(
  transaction: Transaction,
  isDestination: boolean
): { amount: number; symbol: string | undefined } {
  if (isDestination) {
    return {
      amount: transaction.destination_amount,
      symbol: transaction.destination_wallet.currency.symbol,
    };
  }
  return {
    amount: transaction.amount,
    symbol: transaction?.source_wallet?.currency?.symbol,
  };
}

function buildSwapDetail(
  transaction: Transaction,
  trType: Transaction['transaction_method']['type'],
  isCrypto: boolean
) {
  if (trType !== 'SWAP') {
    return {};
  }
  const digits = isCrypto ? 5 : 2;
  return {
    source_amount: fixDecimal({
      value: transaction.amount,
      fractionDigits: digits,
    }),
    source_symbol: transaction?.source_wallet?.currency?.symbol,
    destination_amount: fixDecimal({
      value: transaction.destination_amount,
      fractionDigits: digits,
    }),
    destination_symbol: transaction?.destination_wallet?.currency?.symbol,
  };
}

function buildTransferDetail(
  transaction: Transaction,
  trType: Transaction['transaction_method']['type'],
  isDestination: boolean,
  t: ModalT
) {
  if (trType !== 'TRANSFER') {
    return {};
  }
  if (isDestination) {
    return {
      text: t('fromTransferField'),
      user: transaction.form.transfer.source_identifier_number,
    };
  }
  return {
    text: t('toTransferField'),
    user: transaction.form.transfer.destination_identifier_number,
  };
}

function buildCryptoDetail(
  transaction: Transaction,
  isCrypto: boolean,
  trType: Transaction['transaction_method']['type'],
  t: ModalT
) {
  if (!isCrypto) {
    return {};
  }
  if (trType === 'DEPOSIT') {
    return {
      text: t('fromTxId'),
      id: transaction.form.deposit_crypto?.id,
      txId: transaction.form.deposit_crypto?.txid,
    };
  }
  return {
    text: t('toTxId'),
    id: transaction.form.withdraw_crypto?.id,
    txId: transaction.form.withdraw_crypto?.txid,
  };
}

function computeIsIntermediary(profile: Profile, category: string): boolean {
  const isLDTr = ['DEPOSIT_LD', 'WITHDRAW_LD'].includes(category);
  const isBrokerTr = ['DEPOSIT_BROKER', 'WITHDRAW_BROKER'].includes(category);
  return (
    (profile?.role.title === 'LD' && isLDTr) ||
    (profile?.role.title === 'BROKER' && isBrokerTr)
  );
}

function transactionKindLabel(
  trType: Transaction['transaction_method']['type'],
  isIntermediary: boolean
): string {
  if (trType === 'DEPOSIT') {
    return isIntermediary ? 'Withdraw' : 'Deposit';
  }
  if (trType === 'WITHDRAW') {
    return isIntermediary ? 'Deposit' : 'Withdraw';
  }
  if (trType === 'TRANSFER') {
    return 'Transfer';
  }
  if (trType === 'SWAP') {
    return 'Exchange';
  }
  return '';
}

const FAILED_STATUSES = new Set([
  'UNAUTHORIZED',
  'NOT_CLEARED',
  'DENIED',
  'CANCELED',
]);

function transactionStatusLabel(status: string): string {
  if (FAILED_STATUSES.has(status)) {
    return 'Failed';
  }
  if (status === 'SETTLED') {
    return 'Success';
  }
  return 'Pending';
}

function formatFeeLine(
  transaction: Transaction,
  isCrypto: boolean,
  symbol: string | undefined
): string {
  if (!transaction.fee.fee_amount) {
    return '0';
  }
  return (
    fixDecimal({
      value: transaction.fee.fee_amount,
      fractionDigits: isCrypto ? 5 : 2,
    }) + (symbol ?? '')
  );
}

type DetailBodyProps = {
  transaction: Transaction;
  profile: Profile;
  t: ModalT;
  trType: Transaction['transaction_method']['type'];
  isCrypto: boolean;
  isIntermediary: boolean;
  amountTr: ReturnType<typeof getAmountTr>;
  swapDetail: ReturnType<typeof buildSwapDetail>;
  transferDetail: ReturnType<typeof buildTransferDetail>;
  cryptoDetail: ReturnType<typeof buildCryptoDetail>;
};

const TransactionDetailBody: React.FC<DetailBodyProps> = ({
  transaction,
  profile,
  t,
  trType,
  isCrypto,
  isIntermediary,
  amountTr,
  swapDetail,
  transferDetail,
  cryptoDetail,
}) => {
  const kindLabel = transactionKindLabel(trType, isIntermediary);
  const statusLabel = transactionStatusLabel(transaction.status);
  const fractionDigits = isCrypto ? 5 : 2;
  const amountLine = `${
    fixDecimal({
      value: amountTr.amount,
      fractionDigits,
    }) || '-'
  } ${amountTr.symbol}`;
  const feeLine = formatFeeLine(transaction, isCrypto, amountTr.symbol);
  const showCryptoMeta = isCrypto && (cryptoDetail.id || cryptoDetail.txId);

  return (
    <Flex flexDir="column" gap={4} pb={4}>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={2}>
          <TransactionMethod type={trType} isIntermediary={isIntermediary} />
          <Text color="body">{kindLabel}</Text>
        </Flex>
        <TransactionStatus status={transaction.status} />
      </Flex>
      <Separator borderColor="body2" />
      <Flex align="center" justify="space-between">
        <Text color="body2">{t('status')}</Text>
        <Text fontSize="md" color="body">
          {statusLabel}
        </Text>
      </Flex>
      {trType !== 'SWAP' && (
        <Flex align="center" justify="space-between">
          <Text color="body2">{t('amountFrom')}</Text>
          <Text fontSize="md" color="body">
            {amountLine}
          </Text>
        </Flex>
      )}
      {trType === 'SWAP' && (
        <Flex align="center" justify="space-between">
          <Text color="body2">{t('amountFrom')}</Text>
          <Text fontSize="md" color="body">
            {`${swapDetail.source_amount || '-'} ${swapDetail.source_symbol}`}
          </Text>
        </Flex>
      )}
      {trType === 'SWAP' && (
        <Flex align="center" justify="space-between">
          <Text color="body2">{t('amountTo')}</Text>
          <Text fontSize="md" color="body">
            {`${swapDetail.destination_amount || '-'} ${
              swapDetail.destination_symbol
            }`}
          </Text>
        </Flex>
      )}
      {trType === 'TRANSFER' && (
        <Flex align="center" justify="space-between">
          <Text color="body2">{transferDetail.text}</Text>
          <Text fontSize="md" color="body">
            {transferDetail.user ?? '-'}
          </Text>
        </Flex>
      )}
      {trType !== 'SWAP' && (
        <Flex align="center" justify="space-between">
          <Text color="body2">{t('trFee')}</Text>
          <Text fontSize="md" color="body">
            {feeLine}
          </Text>
        </Flex>
      )}
      <Flex align="center" justify="space-between">
        <Text color="body2">{t('dateTime')}</Text>
        <Text fontSize="md" color="body">
          {dayjs(transaction.created_at).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      </Flex>
      {showCryptoMeta && cryptoDetail.id && (
        <Flex
          w="100%"
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <Text color="body2" flexShrink={0}>
            {t('internalID')}
          </Text>
          <Box flex="1" minW={0}>
            <CopyClipBoard
              initialValue={cryptoDetail.id || ''}
              showCharacterLength={16}
              full
            />
          </Box>
        </Flex>
      )}
      {showCryptoMeta && cryptoDetail.txId && (
        <Flex
          w="100%"
          align="center"
          justify="space-between"
          gap={4}
          flexWrap="wrap"
        >
          <Text color="body2" flexShrink={0}>
            {t('txID')}
          </Text>
          <Box flex="1" minW={0}>
            <CopyClipBoard
              initialValue={cryptoDetail.txId || ''}
              showCharacterLength={16}
              full
            />
          </Box>
        </Flex>
      )}
      {profile.system_identifier === 'XM' &&
        ['DEPOSIT_BROKER:XM', 'WITHDRAW_BROKER:XM'].includes(
          transaction.transaction_method.key
        ) && <XmAdditionalData transaction={transaction} />}
    </Flex>
  );
};

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  transaction,
  profile,
  isOpen,
  onClose,
}: TransactionDetailModalProps) => {
  const { t } = useTranslation('component.dashboard.recentTransactions.modal');

  const trType = transaction.transaction_method.type;
  const category = transaction.transaction_method.category;
  const isDestination = hasDestinationWallet(transaction);
  const isCrypto = ['DEPOSIT_CRYPTO', 'WITHDRAW_CRYPTO', 'SWAP'].includes(
    category
  );
  const isIntermediary = computeIsIntermediary(profile, category);
  const amountTr = getAmountTr(transaction, isDestination);
  const swapDetail = buildSwapDetail(transaction, trType, isCrypto);
  const transferDetail = buildTransferDetail(
    transaction,
    trType,
    isDestination,
    t
  );
  const cryptoDetail = buildCryptoDetail(transaction, isCrypto, trType, t);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) {
          onClose();
          globalThis.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content {...serviceDialogShellProps({ maxW: 'md' })}>
            <ServiceDialogHeader>{t('title')}</ServiceDialogHeader>
            <ServiceDialogCloseTrigger />
            <ServiceDialogBody>
              <TransactionDetailBody
                transaction={transaction}
                profile={profile}
                t={t}
                trType={trType}
                isCrypto={isCrypto}
                isIntermediary={isIntermediary}
                amountTr={amountTr}
                swapDetail={swapDetail}
                transferDetail={transferDetail}
                cryptoDetail={cryptoDetail}
              />
            </ServiceDialogBody>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
export default TransactionDetailModal;
